<template>
  <div v-if="isLive" class="wc-match-card wc-match-card--live">
    <div class="wc-match-card__live-meta">
      <span class="wc-match-card__live-type">{{ fullTypeText }}</span>
    </div>
    <div class="wc-match-card__live-body">
      <div class="wc-match-card__live-team">
        <team-logo class="wc-match-card__live-logo" :src="match.host_team_logo_url" :name="match.host_team_name" />
        <span class="wc-match-card__live-name" :class="{ 'is-highlight': isHostHighlighted }">{{ match.host_team_name || '-' }}</span>
      </div>
      <div class="wc-match-card__live-center">
        <div class="wc-match-card__live-score">
          <span class="wc-match-card__live-score-num" :class="{ 'is-leading': isLeading('host') }">{{ normalScore(match.host_team_score) }}</span>
          <span class="wc-match-card__live-score-colon">:</span>
          <span class="wc-match-card__live-score-num" :class="{ 'is-leading': isLeading('guest') }">{{ normalScore(match.guest_team_score) }}</span>
        </div>
        <div v-if="penaltyText" class="wc-match-card__live-penalty">{{ penaltyText }}</div>
        <div class="wc-match-card__live-status">
          <span class="wc-match-card__live-dot"></span>
          <span>{{ statusText }}</span>
        </div>
      </div>
      <div class="wc-match-card__live-team wc-match-card__live-team--right">
        <team-logo class="wc-match-card__live-logo" :src="match.guest_team_logo_url" :name="match.guest_team_name" />
        <span class="wc-match-card__live-name" :class="{ 'is-highlight': isGuestHighlighted }">{{ match.guest_team_name || '-' }}</span>
      </div>
    </div>
  </div>
  <div v-else class="wc-match-card">
    <div class="wc-match-card__side">
      <div class="wc-match-card__time">{{ matchTime }}</div>
      <div class="wc-match-card__type">{{ typeText }}</div>
    </div>
    <div class="wc-match-card__teams">
      <div class="wc-match-card__team" :class="{ 'is-winner': isWinner('host') }">
        <team-logo :src="match.host_team_logo_url" :name="match.host_team_name" />
        <span class="wc-match-card__name" :class="teamNameClass('host')">{{ match.host_team_name || '-' }}</span>
        <span class="wc-match-card__score" :class="teamScoreClass('host')">{{ scoreText(match.host_team_score, match.host_team_bigscore) }}</span>
      </div>
      <div class="wc-match-card__team" :class="{ 'is-winner': isWinner('guest') }">
        <team-logo :src="match.guest_team_logo_url" :name="match.guest_team_name" />
        <span class="wc-match-card__name" :class="teamNameClass('guest')">{{ match.guest_team_name || '-' }}</span>
        <span class="wc-match-card__score" :class="teamScoreClass('guest')">{{ scoreText(match.guest_team_score, match.guest_team_bigscore) }}</span>
      </div>
    </div>
    <div class="wc-match-card__status" :class="{ 'is-live': String(match.match_status) === '2' }">
      {{ statusText }}
    </div>
  </div>
</template>

<script>
import TeamLogo from './TeamLogo.vue'
import { formatTime } from '../utils/time'

export default {
  name: 'WorldCupMatchCard',
  components: { TeamLogo },
  props: {
    match: {
      type: Object,
      required: true
    },
    compactType: {
      type: Boolean,
      default: false
    },
    pendingText: {
      type: String,
      default: '未开赛'
    },
    highlightTeamId: {
      type: [String, Number],
      default: ''
    }
  },
  computed: {
    matchTime () {
      return formatTime(this.match.date_time || this.match.date)
    },
    typeText () {
      const type = String(this.match.match_type || '')
      const des = this.match.match_type_des || this.match.match_type_name || ''
      if (this.compactType && type === '1' && this.match.group_name) {
        return `${this.match.group_name}组`
      }
      if (type === '1' && this.match.group_name) {
        return `${des} · ${this.match.group_name}组`
      }
      return des
    },
    statusText () {
      const status = String(this.match.match_status || '0')
      if (status === '2') return this.match.match_des || '进行中'
      if (status === '3') return '已结束'
      return this.pendingText
    },
    isLive () {
      return String(this.match.match_status || '0') === '2'
    },
    fullTypeText () {
      const type = String(this.match.match_type || '')
      const des = this.match.match_type_des || this.match.match_type_name || ''
      if (type === '1' && this.match.group_name) {
        return `${this.match.match_type_name || '小组赛'} · ${this.match.group_name}组${des ? ` · ${des}` : ''}`
      }
      return des || this.match.match_type_name || this.typeText
    },
    penaltyText () {
      const host = this.match.host_team_bigscore
      const guest = this.match.guest_team_bigscore
      if (host === '' || host === undefined || host === null || guest === '' || guest === undefined || guest === null) return ''
      return `点球 ${host}:${guest}`
    },
    isHostHighlighted () {
      return this.isHighlighted(this.hostTeamId())
    },
    isGuestHighlighted () {
      return this.isHighlighted(this.guestTeamId())
    },
    isEnded () {
      return String(this.match.match_status || '0') === '3'
    }
  },
  methods: {
    hostTeamId () {
      return this.match.host_team_id || this.match.host_team_info_id || (this.match.host_team_info && this.match.host_team_info.id)
    },
    guestTeamId () {
      return this.match.guest_team_id || this.match.guest_team_info_id || (this.match.guest_team_info && this.match.guest_team_info.id)
    },
    isHighlighted (teamId) {
      return this.highlightTeamId !== '' && teamId !== undefined && teamId !== null && String(teamId) === String(this.highlightTeamId)
    },
    teamNameClass (side) {
      return {
        'is-highlight': side === 'host' ? this.isHostHighlighted : this.isGuestHighlighted,
        'is-winner': this.isWinner(side),
        'is-loser': this.isLoser(side)
      }
    },
    teamScoreClass (side) {
      return {
        'is-winner': this.isWinner(side),
        'is-loser': this.isLoser(side)
      }
    },
    isWinner (side) {
      if (!this.isEnded) return false
      const result = this.compareScores()
      if (result === 0) return false
      return side === 'host' ? result > 0 : result < 0
    },
    isLoser (side) {
      if (!this.isEnded) return false
      const result = this.compareScores()
      if (result === 0) return false
      return side === 'host' ? result < 0 : result > 0
    },
    isLeading (side) {
      if (!this.isLive) return false
      const result = this.compareScores()
      if (result === 0) return false
      return side === 'host' ? result > 0 : result < 0
    },
    compareScores () {
      const hostScore = this.numberValue(this.match.host_team_score)
      const guestScore = this.numberValue(this.match.guest_team_score)
      if (hostScore !== guestScore) return hostScore - guestScore
      const hostBigScore = this.numberValue(this.match.host_team_bigscore)
      const guestBigScore = this.numberValue(this.match.guest_team_bigscore)
      return hostBigScore - guestBigScore
    },
    numberValue (value) {
      const num = Number(value)
      return Number.isFinite(num) ? num : 0
    },
    normalScore (score) {
      return score === '' || score === undefined || score === null ? '-' : score
    },
    scoreText (score, bigScore) {
      const status = String(this.match.match_status || '0')
      if (status === '1' || status === '0') return '-'
      const normalScore = score === '' || score === undefined || score === null ? '-' : score
      if (bigScore !== '' && bigScore !== undefined && bigScore !== null) {
        return `${normalScore} [${bigScore}]`
      }
      return normalScore
    }
  }
}
</script>

<style scoped lang="scss">
.wc-match-card {
  display: grid;
  grid-template-columns: 72px 1fr 56px;
  gap: 10px;
  align-items: center;
  padding: 10px 12px;
  margin-top: 10px;
  border-radius: 8px;
  background: #ffffff;
}

.wc-match-card--live {
  display: block;
  padding: 12px 16px 14px;
  border-radius: 8px;
}

.wc-match-card__live-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 14px;
}

.wc-match-card__live-type {
  padding: 2px 8px;
  border: 1px solid #dce1eb;
  border-radius: 3px;
  color: #8a96a8;
  font-size: 10px;
  line-height: 14px;
}

.wc-match-card__live-body {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 73px;
}

.wc-match-card__live-team {
  display: flex;
  flex: 0 0 84px;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.wc-match-card__live-logo {
  width: 48px;
  height: 48px;
  flex-basis: 48px;
  line-height: 48px;
}

.wc-match-card__live-name {
  width: 100%;
  color: #111318;
  font-size: 13px;
  font-weight: 500;
  line-height: 17px;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.wc-match-card__live-name.is-highlight {
  color: #b8880a;
  font-weight: 700;
}

.wc-match-card__live-center {
  flex: 1 1 auto;
  min-width: 72px;
  text-align: center;
}

.wc-match-card__live-score {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 7px;
  color: #111318;
}

.wc-match-card__live-score-num {
  min-width: 18px;
  font-size: 32px;
  font-weight: 800;
  line-height: 38px;
}

.wc-match-card__live-score-num.is-leading {
  color: #deba40;
}

.wc-match-card__live-score-colon {
  color: #111318;
  font-size: 28px;
  font-weight: 700;
  line-height: 34px;
}

.wc-match-card__live-penalty {
  margin-top: 2px;
  color: #8a96a8;
  font-size: 11px;
  line-height: 14px;
}

.wc-match-card__live-status {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 6px;
  color: #d4231f;
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
}

.wc-match-card__live-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #d4231f;
  animation: wc-live-pulse 1.2s ease-in-out infinite;
}

@keyframes wc-live-pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }

  50% {
    opacity: 0.35;
    transform: scale(0.75);
  }
}

.wc-match-card__time {
  font-size: 16px;
  font-weight: 700;
  color: #1a1a1a;
}

.wc-match-card__type {
  margin-top: 4px;
  color: #9aa0a6;
  font-size: 11px;
  line-height: 16px;
}

.wc-match-card__teams {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.wc-match-card__team {
  display: flex;
  align-items: center;
  min-width: 0;
  height: 28px;
}

.wc-match-card__name {
  flex: 1;
  min-width: 0;
  margin-left: 8px;
  color: #1a1a1a;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.wc-match-card__name.is-highlight {
  color: #b8880a;
  font-weight: 700;
}

.wc-match-card__name.is-winner {
  color: #111318;
  font-weight: 600;
}

.wc-match-card__name.is-loser {
  color: #4a5568;
}

.wc-match-card__score {
  margin-left: 8px;
  color: #1a1a1a;
  font-size: 15px;
  font-weight: 700;
}

.wc-match-card__score.is-winner {
  color: #b8880a;
}

.wc-match-card__score.is-loser {
  color: #4a5568;
}

.wc-match-card__status {
  color: #8a9099;
  font-size: 12px;
  text-align: right;
}

.wc-match-card__status.is-live {
  color: #d4231f;
  font-weight: 700;
}
</style>
